import { useEffect, useRef } from 'react';
import InnerHTML from '@serpro/react-dangerously-set-html-content';
import { withLgpdCookie } from 'govbr.lgpd.plugin/components';
import { blockHTML } from 'govbr.lgpd.plugin/helpers';
import PropTypes from 'prop-types';

const INSTAGRAM_SELECTOR = 'blockquote.instagram-media';
const INSTAGRAM_SCRIPT_ID = 'govbr-instagram-embed-script';
const INSTAGRAM_SCRIPT_SRC = 'https://www.instagram.com/embed.js';

const processInstagramEmbeds = () => {
  if (typeof window === 'undefined') return;
  const processFn = window.instgrm?.Embeds?.process;
  if (typeof processFn === 'function') {
    processFn();
  }
};

const ensureInstagramScript = () => {
  if (typeof document === 'undefined') return;

  const existingScript = document.getElementById(INSTAGRAM_SCRIPT_ID);
  if (existingScript) {
    processInstagramEmbeds();
    return;
  }

  const script = document.createElement('script');
  script.id = INSTAGRAM_SCRIPT_ID;
  script.src = INSTAGRAM_SCRIPT_SRC;
  script.async = true;
  script.onload = () => {
    processInstagramEmbeds();
  };

  document.body.appendChild(script);
};

const View = ({
  data,
  performanceCookies,
  thirdPartyCookies,
  hasLgpdCookie,
  isClient,
  groupDomains,
}) => {
  const containerRef = useRef(null);

  const blockObject = blockHTML(
    data.html,
    performanceCookies,
    thirdPartyCookies,
    isClient,
    groupDomains,
    { allowScriptContent: true },
  );

  useEffect(() => {
    if (!isClient || !hasLgpdCookie || !thirdPartyCookies) return;
    if (!containerRef.current) return;

    const hasInstagramEmbed =
      !!containerRef.current.querySelector(INSTAGRAM_SELECTOR);
    if (!hasInstagramEmbed) return;

    ensureInstagramScript();

    const animationFrameId = window.requestAnimationFrame(() => {
      processInstagramEmbeds();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [blockObject.html, hasLgpdCookie, isClient, thirdPartyCookies]);

  return (
    <div ref={containerRef} className="block html">
      {blockObject.html ? (
        <InnerHTML html={blockObject.html} isClient={isClient} />
      ) : null}
    </div>
  );
};

View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  groupDomains: PropTypes.object,
  hasLgpdCookie: PropTypes.bool,
  thirdPartyCookies: PropTypes.bool,
};

export default withLgpdCookie(View);
